import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import MetricsOverview from './components/MetricsOverview';
import IncidentTrendChart from './components/IncidentTrendChart';
import SeverityPanel from './components/SeverityPanel';
import PersonalAssignments from './components/PersonalAssignments';
import QuickFilters from './components/QuickFilters';
import RecentActivity from './components/RecentActivity';

const MainDashboard = () => {
  const navigate = useNavigate();
  const [activeFilters, setActiveFilters] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [incidents, setIncidents] = useState([]);
  const [metricsData, setMetricsData] = useState({});
  const [incidentCounts, setIncidentCounts] = useState({});
  const [severityData, setSeverityData] = useState({});
  const [trendData, setTrendData] = useState({ daily: [], weekly: [], monthly: [] });
  const [personalAssignments, setPersonalAssignments] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);

  // Mock current user data (can be replaced with auth context)
  const currentUser = {
    id: "USR001",
    name: "Sarah Chen",
    role: "Senior Security Analyst",
    permissions: ["view_dashboard", "view_incidents", "create_incident", "view_reports", "view_settings"]
  };

  useEffect(() => {
    const fetchIncidents = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_APP_SERVER_URL}/incident`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setIncidents(data);
        processIncidentData(data);
      } catch (error) {
        console.error("Could not fetch incidents:", error);
      }
    };
    fetchIncidents();

    const interval = setInterval(() => {
      fetchIncidents();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const processIncidentData = (data) => {
    // 1. Calculate Metrics Overview
    const today = new Date();
    const resolvedTodayCount = data.filter(inc => 
      inc.status === 'resolved' && new Date(inc.resolvedAt).toDateString() === today.toDateString()
    ).length;
    const activeIncidentsCount = data.filter(inc => inc.status !== 'resolved').length;
    const slaBreachesCount = data.filter(inc => 
      inc.slaDeadline && new Date(inc.slaDeadline) < today && inc.status !== 'resolved'
    ).length;

    setMetricsData({
      activeIncidents: activeIncidentsCount,
      slaBreaches: slaBreachesCount,
      resolvedToday: resolvedTodayCount,
      systemHealth: 98 // This is likely an external metric, so we'll keep it static for this example
    });

    // 2. Calculate Incident and Severity Counts
    const counts = { critical: 0, high: 0, medium: 0, low: 0 };
    data.forEach(inc => {
      if (counts[inc.severity.toLowerCase()] !== undefined) {
        counts[inc.severity.toLowerCase()]++;
      }
    });
    setIncidentCounts(counts);
    setSeverityData(counts);

    // 3. Populate Personal Assignments
    const userAssignments = data.filter(inc => inc.assigneeId === currentUser.id);
    setPersonalAssignments(userAssignments);

    // 4. Populate Recent Activities
    // This is a simplified approach; in a real app, you'd fetch an activity log.
    // For now, we'll use the incidents themselves as a proxy for recent activity.
    const sortedIncidents = [...data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const recent = sortedIncidents.slice(0, 8).map(inc => ({
      id: `ACT-${inc.id}`,
      type: "incident_created", // Or a more specific type if the data contains it
      title: inc.title,
      description: inc.description,
      user: inc.reporterName || "System", // Assuming a reporter field exists
      timestamp: new Date(inc.createdAt),
      incidentId: inc.id,
      severity: inc.severity
    }));
    setRecentActivities(recent);

    // 5. Calculate Incident Trend Data
    // This requires more complex logic to group incidents by date.
    // Here's a simplified example for daily trends.
    const dailyTrends = {};
    data.forEach(inc => {
      const day = new Date(inc.createdAt).toLocaleDateString('en-US', { weekday: 'short' });
      if (!dailyTrends[day]) {
        dailyTrends[day] = { period: day, critical: 0, high: 0, medium: 0, low: 0 };
      }
      dailyTrends[day][inc.severity.toLowerCase()]++;
    });
    setTrendData({
      daily: Object.values(dailyTrends),
      weekly: [], // Add logic to calculate weekly data if needed
      monthly: [] // Add logic to calculate monthly data if needed
    });
  };

  const handleCreateIncident = () => {
    navigate('/create-incident');
  };

  const handleFilterChange = (filters) => {
    setActiveFilters(filters);
    // This would trigger a re-fetch or re-process with filters
    console.log('Filters changed:', filters);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={currentUser} 
        incidentCounts={incidentCounts}
      />
      <main className="nav-offset">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Welcome back, {currentUser?.name}
            </h1>
            <p className="text-lg text-muted-foreground">
              Here's your cybersecurity incident overview for today
            </p>
          </div>

          {/* Metrics Overview */}
          <MetricsOverview 
            metrics={metricsData}
            onCreateIncident={handleCreateIncident}
          />

          {/* Quick Filters */}
          <QuickFilters 
            onFilterChange={handleFilterChange}
            activeFilters={activeFilters}
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Left Column - Charts and Severity */}
            <div className="lg:col-span-2 space-y-8">
              <IncidentTrendChart trendData={trendData} />
              <SeverityPanel severityData={severityData} />
            </div>

            {/* Right Column - Assignments and Activity */}
            <div className="space-y-8">
              <PersonalAssignments 
                assignments={personalAssignments}
                currentUser={currentUser}
              />
              <RecentActivity activities={recentActivities} />
            </div>
          </div>

          {/* Footer Stats */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-light">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-2xl font-bold text-foreground mb-1">
                  {new Date()?.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                </div>
                <div className="text-sm text-muted-foreground">Today's Date</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary mb-1">24/7</div>
                <div className="text-sm text-muted-foreground">Monitoring</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-success mb-1">99.9%</div>
                <div className="text-sm text-muted-foreground">Uptime</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-accent mb-1">
                  &lt; 15min
                </div>
                <div className="text-sm text-muted-foreground">Avg Response</div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MainDashboard;